import { useQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { AggregateDataCheckbox as AggregateSpendCheckbox } from '@/components/ui/aggregate-data-checkbox';
import { useOpeningContext } from '@/contexts/opening-context';
import { useOpeningSlug } from '@/hooks/use-opening-slug';

import { AggregateView } from './aggregate-view';

export function SourcingOpenings() {
  const intl = useIntl();
  const openingSlug = useOpeningSlug();
  const {
    showAggregateData,
    setShowAggregateHiresCheckbox,
    currentHiringGoalId,
    showAggregateSpend,
    setShowAggregateSpend,
  } = useOpeningContext();

  const { data, isFetching, isError } = useQuery({
    queryKey: [
      'sourcingFunnels',
      openingSlug,
      currentHiringGoalId,
      showAggregateData,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsSourcingFunnels(
        openingSlug,
        currentHiringGoalId,
        showAggregateData,
      ),
  });

  if (isError) throw new Error('Failed to fetch sourcing funnels');

  const sourcingOpenings = useMemo(() => data?.sourcing_funnels ?? [], [data]);
  const aggregateData = useMemo(
    () => (data && data.aggregate ? [data.aggregate] : []),
    [data],
  );

  useEffect(() => {
    if (sourcingOpenings.length > 0) {
      setShowAggregateHiresCheckbox(true);
    }
  }, [setShowAggregateHiresCheckbox, sourcingOpenings]);

  if (!isFetching && aggregateData.length === 0) return null;

  return (
    <>
      <div className="flex justify-end">
        <AggregateSpendCheckbox
          checked={showAggregateSpend}
          onCheckedChange={value => setShowAggregateSpend(!!value)}
          label={intl.formatMessage({
            defaultMessage: 'Include this to “Budget Spend”',
          })}
        />
      </div>
      <div className="w-full">
        <div className="overflow-x-auto">
          <div className="min-w-max gap-1">
            <div className="space-y-4 pb-2">
              <AggregateView
                aggregateData={aggregateData}
                sourcingOpenings={sourcingOpenings}
                isFetching={isFetching}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
